import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, BarChart3, Package, Users, Clock, Monitor, ShoppingBag, Utensils, Scissors, CheckCircle } from "lucide-react";
import LeadForm from "@/forms/engine/LeadForm";
import eposDemo from "@/forms/definitions/eposDemo";
import RelatedReviews from "@/components/RelatedReviews";
import LocationCrossLinks from "@/components/LocationCrossLinks";
import FormReviewCompanion from "@/components/FormReviewCompanion";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useProductViewed } from "@/utils/trackingHooks";
import SEOHead from "@/components/SEOHead";
import WhatsAppButton from "@/components/WhatsAppButton";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { whatsAppUrl } from "@/utils/whatsapp";
import analytics from "@/utils/analytics";
import { fadeUp, stagger, heroImageMotion, viewportOnce, viewportOnceSmall } from "@/utils/motionVariants";

const EPOS_SOLUTIONS = [
  { name: "Register", desc: "Full-featured EPOS trusted by thousands of UK businesses. Integrated payments, inventory, and reporting.", img: "https://cdn.candopayments.com/marketing/heroes/epos-register_colour_web_solid.jpg", to: "/epos-systems/register", cta: "Explore Register" },
  { name: "Smart POS Solutions", desc: "All-in-one smart terminals that combine payment processing with point-of-sale functionality.", img: "https://cdn.candopayments.com/marketing/heroes/epos-smart-pos_colour_web_solid.jpg", to: "/contact", cta: "Get a Quote" },
];

const INDUSTRIES = [
  { icon: Utensils, title: "Hospitality", features: ["Table management & ordering", "Kitchen display integration", "Online ordering & delivery", "Pay-at-table service", "Real-time stock control"] },
  { icon: ShoppingBag, title: "Retail", features: ["Multi-site management", "Click & Collect integration", "Loyalty & rewards schemes", "Automated stocktaking", "Sales staff performance"] },
  { icon: Scissors, title: "Services", features: ["Appointment booking", "Text reminders & no-show reduction", "Mobile clinician payments", "Customer loyalty schemes", "Client database management"] },
  { icon: Monitor, title: "E-Commerce", features: ["Online & in-store sync", "Delivery & subscription management", "Real-time stock across channels", "Customer behaviour insights", "Integrated rewards programs"] },
];

export default function EposSystemsPage() {
  useProductViewed("epos");
  return (
    <div>
      <SEOHead title="Best EPOS Systems for UK Businesses" description="Upgrade to the top EPOS systems in the UK. At CanDo Payments, our point-of-sale technology streamlines sales, manages stock and helps your business to grow." path="/epos-systems" service={{ name: "EPOS Systems", description: "Integrated EPOS systems for UK hospitality, retail and service businesses. Sales, stock, and staff management with UK-based support.", serviceType: "Point of Sale Systems" }} />
      {/* Hero */}
      <section className="hero-gradient py-16 md:py-24" data-testid="epos-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <Breadcrumbs className="mb-5" />
              <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">EPOS Systems</motion.p>
              <motion.h1 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-6">
                One system to manage <span className="text-brand-aqua">your entire business</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-brand-grey leading-relaxed mb-8">
                EPOS systems that go beyond payments. Manage sales, stock, staff, and customers from one place — with real-time data and UK-based support whenever you need it.
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
                <a href="#solutions" className="bg-brand-aqua hover:bg-brand-aqua-dark text-white font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="epos-cta-solutions">
                  View Solutions <ArrowRight size={16} />
                </a>
                <Link to="/contact?product=EPOS%20System&category=epos-systems&source=epos-page" className="bg-white text-brand-black hover:bg-gray-50 border border-brand-light-grey font-semibold rounded-full px-8 py-3.5 text-sm transition-colors" data-testid="epos-cta-quote">
                  Get a Quote
                </Link>
                <Link to="/quote/in-person" className="bg-brand-pink-light text-brand-pink hover:bg-brand-pink/15 border border-brand-pink/30 font-semibold rounded-full px-8 py-3.5 text-sm flex items-center gap-2 transition-colors" data-testid="epos-cta-estimate">
                  Get an Instant Estimate
                </Link>
              </motion.div>
            </motion.div>
            <motion.div {...heroImageMotion} className="hidden lg:block">
              <img
                src="https://cdn.candopayments.com/marketing/heroes/epos-systems_colour_web_solid.png"
                alt="EPOS System"
                className="w-full max-w-md mx-auto rounded-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits bar */}
      <section className="py-12 bg-white border-b border-brand-light-grey/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { icon: BarChart3, label: "Real-Time Reporting" },
              { icon: Package, label: "Stock Management" },
              { icon: Users, label: "Staff Controls" },
              { icon: WhatsAppIcon, label: "WhatsApp Support (<4 min)", href: whatsAppUrl("epos") },
            ].map((item) => {
              const inner = (
                <>
                  <item.icon size={20} className="text-brand-aqua flex-shrink-0" />
                  <span className="text-sm font-semibold text-brand-black">{item.label}</span>
                </>
              );
              return item.href ? (
                <a
                  key={item.label}
                  href={item.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => analytics.clickWhatsApp("epos")}
                  className="flex items-center gap-3 p-4 rounded-xl bg-brand-aqua-light hover:bg-brand-aqua/20 transition-colors"
                  data-testid="epos-feature-whatsapp"
                >
                  {inner}
                </a>
              ) : (
                <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl bg-brand-aqua-light">
                  {inner}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* EPOS by Industry */}
      <section className="py-20 md:py-28 bg-gray-50" data-testid="epos-industries">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">EPOS For Your Industry</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">
              Tailored for the way you work
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {INDUSTRIES.map((ind, i) => (
              <motion.div key={ind.title} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`epos-industry-${ind.title.toLowerCase()}`}>
                <div className="w-12 h-12 rounded-xl bg-brand-aqua-light flex items-center justify-center mb-4">
                  <ind.icon size={22} className="text-brand-aqua" />
                </div>
                <h3 className="font-heading text-lg font-bold text-brand-black mb-3">{ind.title}</h3>
                <ul className="space-y-2">
                  {ind.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-brand-grey">
                      <CheckCircle size={13} className="text-brand-aqua mt-0.5 flex-shrink-0" />{f}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="py-20 md:py-28 bg-white" data-testid="epos-solutions">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnce} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua mb-3">Our EPOS Solutions</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading text-3xl sm:text-4xl font-bold text-brand-black tracking-tight">
              Choose the right EPOS for your business
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={viewportOnceSmall} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {EPOS_SOLUTIONS.map((sol) => (
              <motion.div key={sol.name} variants={fadeUp} className="bg-white border border-brand-light-grey rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group" data-testid={`epos-solution-${sol.name.toLowerCase().replace(/\s+/g, '-')}`}>
                <Link to={sol.to} aria-label={sol.name} data-testid={`epos-solution-img-${sol.name.toLowerCase().replace(/\s+/g, '-')}`}>
                  <div className="aspect-video overflow-hidden">
                    <img src={sol.img} alt={sol.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                </Link>
                <div className="p-8">
                  <h3 className="font-heading text-2xl font-bold text-brand-black mb-3">
                    <Link to={sol.to} className="hover:text-brand-aqua-dark transition-colors">{sol.name}</Link>
                  </h3>
                  <p className="text-brand-grey leading-relaxed mb-4">{sol.desc}</p>
                  <Link to={sol.to} className="text-brand-aqua font-semibold text-sm flex items-center gap-1 hover:gap-2 transition-all" data-testid={`epos-solution-cta-${sol.name.toLowerCase().replace(/\s+/g, '-')}`}>
                    {sol.cta}
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-gray-50" data-testid="epos-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="font-heading text-3xl font-bold text-brand-black tracking-tight mb-6">
                Find the best EPOS for your business
              </h2>
              <p className="text-brand-grey leading-relaxed mb-6">
                Not sure which system is right for you? Our team will listen to how you work and recommend the best setup — with clear pricing and no pressure.
              </p>
              <div className="space-y-3">
                {["Easy to train staff — on-site or virtual", "WhatsApp support with <4 minute response", "Manage your business from any device", "Real-time data on performance and stock"].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-brand-black">
                    <CheckCircle size={16} className="text-brand-aqua flex-shrink-0" />{item}
                  </div>
                ))}
              </div>
              <div className="mt-5">
                <WhatsAppButton variant="link" placement="epos" label="WhatsApp us about EPOS" />
              </div>
            </div>
            <div className="space-y-5">
              <div className="bg-white border border-brand-light-grey rounded-2xl p-6 sm:p-8 shadow-sm">
                <h3 className="font-heading text-xl font-bold text-brand-black mb-4">Book a free EPOS demo</h3>
                <LeadForm schema={eposDemo} context={{ page: "EPOS systems" }} />
              </div>
              <FormReviewCompanion product="epos-systems" testId="epos-systems-form-review" />
            </div>
          </div>
        </div>
      </section>

      <LocationCrossLinks />
      <RelatedReviews
        product="epos-systems"
        eyebrow="Customer Stories"
        heading="See what our customers say about our EPOS systems"
        subheading="Real feedback from UK businesses running their day on a CanDo EPOS."
        testId="epos-related-reviews"
      />
    </div>
  );
}
